import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Home, KeyRound, ShieldCheck, Star, MapPin, Zap, Award, TrendingUp, Users, Database } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { useBrand } from "@/lib/brand";

const TRUST_ICONS = {
  "shield-check": ShieldCheck,
  "star": Star,
  "home": Home,
  "map-pin": MapPin,
  "zap": Zap,
  "award": Award,
  "trending-up": TrendingUp,
  "users": Users,
  "database": Database,
};

const DEFAULT_TRUST_BADGES = [
  { icon: "shield-check", text: "PRS & CMP regulated" },
  { icon: "star", text: "5-star reviews across Surrey" },
  { icon: "home", text: "Independent, local, personal" },
];

export default function Landing() {
  const brand = useBrand();
  const biz = brand.business;
  const heroImg = brand.assets?.hero_image_url;
  const heroCaption = brand.assets?.hero_caption || biz.tagline;
  const areas = brand.areas_served || [];
  const badges = brand.regulatory?.badges || [];
  const testimonial = brand.testimonial;
  const trustBadges = brand.trust_badges && brand.trust_badges.length > 0
    ? brand.trust_badges
    : DEFAULT_TRUST_BADGES;
  const phoneHref = `tel:${(biz.phone_intl || biz.phone || "").replace(/\s/g, "")}`;
  // Tenant-aware valuation URL — /t/<slug>/valuation ONLY when the user
  // is already on a /t/<slug>/... path. Legacy /try, /embed continue to
  // link to the legacy /valuation route so the first customer's old iframes and
  // bookmarks aren't silently rewritten.
  const isTenantScopedPath = typeof window !== "undefined"
    && window.location.pathname.startsWith("/t/");
  const valuationHref = isTenantScopedPath && brand.tenant_slug
    ? `/t/${brand.tenant_slug}/valuation`
    : "/valuation";
  const registerHref = isTenantScopedPath && brand.tenant_slug
    ? `/t/${brand.tenant_slug}/register`
    : "/register";

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SiteHeader />

      {/* Hero */}
      <section data-testid="hero-section" className="relative overflow-hidden">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 pt-14 pb-20 lg:pt-24 lg:pb-32 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6 fade-in">
            <span className="pill-tag" data-testid="hero-eyebrow">60-Second Online Valuation</span>
            <h1 className="mt-6 font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-tight text-primary">
              {biz.hero_headline || "What is your home really worth?"}
            </h1>
            <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed">
              {biz.hero_sub || "A 60-second online valuation for homeowners and landlords."}
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Link to={valuationHref} data-testid="hero-start-cta">
                <Button className="h-12 px-7 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 transition-colors duration-300">
                  Start my valuation <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to={registerHref} data-testid="hero-register-cta">
                <Button variant="outline" className="h-12 px-7 rounded-sm border-primary/20 hover:bg-secondary/40 transition-colors duration-300">
                  Register as a buyer
                </Button>
              </Link>
              {biz.phone && (
                <a href={phoneHref} data-testid="hero-call-cta">
                  <Button variant="ghost" className="h-12 px-7 rounded-sm hover:bg-secondary/30 transition-colors duration-300">
                    Or call {biz.phone}
                  </Button>
                </a>
              )}
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-muted-foreground">
              {trustBadges.map((b, i) => {
                const Icon = TRUST_ICONS[b.icon] || ShieldCheck;
                return (
                  <div key={i} className="flex items-center gap-2">
                    <Icon className="w-4 h-4 text-accent" /> {b.text}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="lg:col-span-6 relative">
            <div className="relative aspect-[4/5] w-full overflow-hidden rounded-sm border border-border shadow-md">
              {heroImg && <img src={heroImg} alt={heroCaption} className="absolute inset-0 h-full w-full object-cover" />}
              <div className="absolute inset-0 bg-gradient-to-t from-primary/60 via-primary/10 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 pt-6 px-6 pb-8 md:pb-28 lg:pt-8 lg:px-8 lg:pb-32 text-primary-foreground">
                <div className="text-xs uppercase tracking-[0.24em] opacity-80">{heroCaption}</div>
                <div className="mt-2 font-serif-display text-2xl lg:text-3xl leading-tight">{biz.tagline}</div>
              </div>
            </div>
            <div className="absolute -bottom-6 -left-4 hidden md:block bg-background border border-border rounded-sm px-5 py-4 shadow-md">
              <div className="text-xs uppercase tracking-[0.24em] text-muted-foreground">Free · No obligation</div>
              <div className="font-serif-display text-lg text-primary mt-1">Full report in 60 seconds</div>
            </div>
          </div>
        </div>
      </section>

      {/* Areas */}
      {areas.length > 0 && (
        <section className="border-y border-border bg-secondary/25">
          <div className="mx-auto max-w-7xl px-6 lg:px-10 py-10 flex flex-wrap items-center gap-x-8 gap-y-3">
            <div className="text-xs uppercase tracking-[0.24em] text-muted-foreground">Serving</div>
            {areas.map((a) => (
              <div key={a} className="flex items-center gap-2 text-sm text-primary" data-testid={`area-${a.toLowerCase().replace(/\s/g, "-")}`}>
                <MapPin className="w-3.5 h-3.5 text-accent" />
                {a}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* How it works */}
      <section data-testid="how-section" className="mx-auto max-w-7xl px-6 lg:px-10 py-20 lg:py-28 grid lg:grid-cols-3 gap-8">
        {[
          { icon: <Home className="w-5 h-5" />, title: "Tell us about the property", body: "A few quick questions — location, size, condition, tenure and features." },
          { icon: <KeyRound className="w-5 h-5" />, title: "We assess the market", body: "Local price data combined with an AI-assisted market view for your postcode." },
          { icon: <ShieldCheck className="w-5 h-5" />, title: "See your indicative range", body: "A refined estimate delivered instantly, and the option to book an in-person valuation." },
        ].map((s, i) => (
          <div key={i} className="border border-border bg-card p-8 rounded-sm">
            <div className="flex items-center gap-3 text-accent">
              <div className="w-9 h-9 rounded-sm bg-secondary/60 flex items-center justify-center">{s.icon}</div>
              <div className="text-xs uppercase tracking-[0.24em] text-muted-foreground">Step {i + 1}</div>
            </div>
            <h3 className="mt-5 font-serif-display text-2xl text-primary">{s.title}</h3>
            <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{s.body}</p>
          </div>
        ))}
      </section>

      {/* Testimonial strip */}
      {testimonial && (
        <section className="bg-primary text-primary-foreground">
          <div className="mx-auto max-w-4xl px-6 lg:px-10 py-20 text-center">
            <div className="text-xs uppercase tracking-[0.28em] text-primary-foreground/60">Client stories</div>
            <p className="mt-6 font-serif-display text-2xl sm:text-3xl leading-snug">
              “{testimonial.quote}”
            </p>
            {testimonial.attribution && <div className="mt-6 text-sm text-primary-foreground/70">{testimonial.attribution}</div>}
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="mx-auto max-w-3xl px-6 lg:px-10 py-24 text-center">
        <h2 className="font-serif-display text-3xl sm:text-4xl text-primary">Ready to see your figure?</h2>
        <p className="mt-4 text-muted-foreground">It only takes 60 seconds and there’s no obligation.</p>
        <Link to={valuationHref} data-testid="footer-cta-start">
          <Button className="mt-8 h-12 px-8 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 transition-colors duration-300">
            Start my valuation <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </Link>
      </section>

      {/* Regulatory badges */}
      {badges.length > 0 && (
        <section data-testid="badges-section" className="border-t border-border bg-secondary/20">
          <div className="mx-auto max-w-5xl px-6 lg:px-10 py-10 grid grid-cols-3 gap-8 items-center">
            {badges.slice(0, 3).map((b) => (
              <img
                key={b.label}
                src={b.url}
                alt={b.label}
                className="max-h-14 mx-auto object-contain opacity-80"
                data-testid={`badge-${b.label.toLowerCase().replace(/\s/g, "-")}`}
              />
            ))}
          </div>
        </section>
      )}

      <SiteFooter />
    </div>
  );
}
